import { Link } from "react-router-dom";
import { MapPin, BadgeCheck, Star, ChevronRight } from "lucide-react";
import type { Listing } from "@/lib/types";
import { getStateByNameOrAbbr } from "@/data/states";

export default function SpaProfileHero({ listing }: { listing: Listing }) {
  const location = [listing.city, listing.state].filter(Boolean).join(", ");
  const fullLocation = listing.address
    ? `${listing.address}, ${location}${listing.zip ? ` ${listing.zip}` : ""}`
    : `${location}${listing.zip ? ` ${listing.zip}` : ""}`;

  return (
    <section>
      {/* Hero bar */}
      <div className="relative bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />

        <div className="relative container mx-auto px-4 lg:px-8 py-10 lg:py-12">
          <div className="text-center">
            {/* Name + badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
              <h1 className="font-display text-3xl lg:text-4xl font-bold text-white">
                {listing.name}
              </h1>
              {listing.tier !== "free" && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-medium">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Verified
                </span>
              )}
              {listing.is_featured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-medium">
                  <Star className="w-3.5 h-3.5 text-white" />
                  Featured
                </span>
              )}
            </div>

            {/* Address */}
            {fullLocation && (
              <div className="flex items-center justify-center gap-1.5 text-sm text-white/80">
                <MapPin className="w-4 h-4" />
                {fullLocation}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="border-b border-border bg-card/50">
        <div className="container mx-auto px-4 lg:px-8 py-3">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground overflow-x-auto scrollbar-hide whitespace-nowrap">
            <Link to="/" className="shrink-0 hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
            <Link to="/browse" className="shrink-0 hover:text-foreground transition-colors">Browse</Link>
            {(() => {
              const stateData = getStateByNameOrAbbr(listing.state);
              if (!stateData) return null;
              return (
                <>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                  <Link
                    to={`/browse/${stateData.slug}`}
                    className="shrink-0 hover:text-foreground transition-colors"
                  >
                    {stateData.name}
                  </Link>
                </>
              );
            })()}
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
            <span className="text-foreground font-medium truncate min-w-0">{listing.name}</span>
          </nav>
        </div>
      </div>

    </section>
  );
}
